// storage — the adapters the offline queues persist through.
//
// Three adapters, one shape, so queue.js and photos.js never know which one
// they got:
//
//   { all(): Promise<record[]>, put(record): Promise, remove(key): Promise }
//
// Records are plain objects with a `key`. IndexedDB is the real one (database
// `fw-field`, stores `ops` and `photos`, both created in the same upgrade so
// either queue can open the database first). localStorage is the fallback for
// JSON-safe records when IndexedDB is missing or refuses to open (private
// mode on some browsers). Memory is what tests inject and what photo blobs
// fall back to — a blob does not survive JSON.
//
// Nothing here is React and nothing here knows what an op is.

export const DB_NAME = 'fw-field';
export const STORES = ['ops', 'photos'];

export function hasIndexedDB() {
  try { return typeof indexedDB !== 'undefined' && indexedDB !== null; } catch (_) { return false; }
}

let dbPromise = null;

function openDb() {
  if (!dbPromise) {
    dbPromise = new Promise((resolve, reject) => {
      let req;
      try { req = indexedDB.open(DB_NAME, 1); } catch (e) { reject(e); return; }
      req.onupgradeneeded = () => {
        const db = req.result;
        for (const name of STORES) {
          if (!db.objectStoreNames.contains(name)) db.createObjectStore(name, { keyPath: 'key' });
        }
      };
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => reject(req.error || new Error('IndexedDB refused to open'));
      req.onblocked = () => reject(new Error('IndexedDB blocked'));
    });
    dbPromise.catch(() => { dbPromise = null; });
  }
  return dbPromise;
}

/** IndexedDB-backed store for one object store. */
export function indexedDbStore(storeName) {
  const run = async (mode, op) => {
    const db = await openDb();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(storeName, mode);
      const req = op(tx.objectStore(storeName));
      tx.oncomplete = () => resolve(req ? req.result : undefined);
      tx.onerror = () => reject(tx.error || new Error('IndexedDB transaction failed'));
      tx.onabort = () => reject(tx.error || new Error('IndexedDB transaction aborted'));
    });
  };
  return {
    kind: 'indexeddb',
    all: () => run('readonly', (s) => s.getAll()).then((rows) => rows || []),
    put: (record) => run('readwrite', (s) => s.put(record)).then(() => undefined),
    remove: (key) => run('readwrite', (s) => s.delete(key)).then(() => undefined),
  };
}

/** localStorage-backed store: one JSON array under `key`. JSON-safe records only. */
export function localStorageStore(key) {
  const read = () => {
    try {
      const raw = localStorage.getItem(key);
      const parsed = raw ? JSON.parse(raw) : [];
      return Array.isArray(parsed) ? parsed : [];
    } catch (_) { return []; }
  };
  const write = (rows) => {
    try { localStorage.setItem(key, JSON.stringify(rows)); } catch (_) { /* full or private */ }
  };
  return {
    kind: 'localstorage',
    all: async () => read(),
    put: async (record) => {
      const rows = read().filter((r) => r.key !== record.key);
      rows.push(record);
      write(rows);
    },
    remove: async (k) => write(read().filter((r) => r.key !== k)),
  };
}

/** In-memory store. Tests inject it; photo blobs fall back to it. */
export function memoryStore(initial = []) {
  const rows = new Map(initial.map((r) => [r.key, r]));
  return {
    kind: 'memory',
    all: async () => Array.from(rows.values()),
    put: async (record) => { rows.set(record.key, record); },
    remove: async (key) => { rows.delete(key); },
  };
}

/**
 * The store a queue should use in this browser: IndexedDB when it exists,
 * otherwise the named fallback. `jsonSafe` says whether localStorage is an
 * acceptable fallback (ops yes, blobs no).
 */
export function defaultStore(storeName, { jsonSafe = true } = {}) {
  if (hasIndexedDB()) return indexedDbStore(storeName);
  if (jsonSafe) return localStorageStore(`fw_field_${storeName}`);
  return memoryStore();
}

/** A store that falls back when IndexedDB fails at runtime, not just at feature-detect. */
export function resilientStore(storeName, opts) {
  const primary = defaultStore(storeName, opts);
  if (primary.kind !== 'indexeddb') return primary;
  let fallback = null;
  const swap = () => {
    if (!fallback) fallback = (opts && opts.jsonSafe === false) ? memoryStore() : localStorageStore(`fw_field_${storeName}`);
    return fallback;
  };
  const guard = (fn) => async (...args) => {
    if (fallback) return fallback[fn](...args);
    try { return await primary[fn](...args); } catch (_) { return swap()[fn](...args); }
  };
  return { kind: 'resilient', all: guard('all'), put: guard('put'), remove: guard('remove') };
}

/** A v4-shaped id. crypto.randomUUID where it exists; a Math.random fallback elsewhere. */
export function uuid() {
  try {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') return crypto.randomUUID();
  } catch (_) { /* fall through */ }
  let out = '';
  const hex = '0123456789abcdef';
  for (let i = 0; i < 36; i++) {
    if (i === 8 || i === 13 || i === 18 || i === 23) out += '-';
    else if (i === 14) out += '4';
    else if (i === 19) out += hex[(Math.random() * 4 | 0) + 8];
    else out += hex[Math.random() * 16 | 0];
  }
  return out;
}
